import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { DisplayProductDataService } from '../../../display-product-data/display-product-data.service';
import { PictureCreateComponent } from '../picture-create/picture-create.component';
import { PictureViewComponent } from '../picture-view/picture-view.component';
import { environment } from '../../../../environments/environment';
import { nameSort } from '../../util';
import { PictureItem } from '../../../display-product-data/controllers/controller.model';

@Component({
    selector: 'app-picture-select-new',
    templateUrl: './picture-select-new.component.html',
    styleUrls: ['./picture-select-new.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PictureSelectNewComponent),
            multi: true
        }
    ],
    standalone: false
})
export class PictureSelectNewComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() fieldName = '';

  pictureList: PictureItem[] = [];
  pictureStore = '';
  pictureNamesList: string[] = [];
  private subscriptions: Subscription[] = [];
  private listLoaded = false;
  private pendingValue: string = ''; // Store value received before list loads

  showNotAvailable = false;

  @Output() selectionChange = new EventEmitter<MatSelectChange>();
  @Output() urlChange = new EventEmitter<MatSelectChange>();

  selectListControl = new UntypedFormControl('');

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private displayProductData: DisplayProductDataService
  ) { }

  ngOnInit() {
    this.displayProductData.getPictureAndDrawingList()
      .subscribe(result => {
        result = result.filter(item => item.type.toLowerCase() === 'picture');
        const pictureNames = nameSort(result);
        this.pictureList = pictureNames;

        // Clear the list to avoid duplicates if ngOnInit runs multiple times
        this.pictureNamesList = [];
        this.pictureList.forEach(item => this.pictureNamesList.push(item.name));
        this.listLoaded = true;
        
        // Now that the list is loaded, apply any pending preselection
        if (this.pendingValue) {
          this.updatePreselection();
          this.pendingValue = '';
        }
      });
  }

  private updatePreselection() {
    if (!this.listLoaded) return;
    
    if (this.pictureNamesList.includes(this.pictureStore)) {
      this.showNotAvailable = false;
      this.selectListControl.setValue(this.pictureStore, { emitEvent: false });
    } else if (this.pictureStore) {
      this.showNotAvailable = true;
      this.selectListControl.setValue('notAvailable', { emitEvent: false });
    }
  }

  pictureChangeAction(event: MatSelectChange<any>) {
    if (event.value === 'create') {
      const dialogRef = this.dialog.open(PictureCreateComponent, {
        width: '1000px',
        maxWidth: '96vw',
        minHeight: '60vh',
        maxHeight: '60vh',
        disableClose: true,
        data: {
          type: 'picture'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.pictureList.unshift(result);
          this.selectListControl.setValue(result.name);
        } else {
          this.selectListControl.setValue(this.pictureStore);
        }
      });
    } else if (event.value === 'empty') {
      this.pictureStore = '';
      this.selectListControl.reset('');
    } else {
      this.pictureStore = event.value;
    }

    this.selectionChange.emit(event);
    const url = event.value ? this.getImageUrl(this.pictureList.find(item => item.name === event.value)) : '';
    this.urlChange.emit({ value: url } as MatSelectChange);
  }

  openViewDialog() {

    const picture = this.selectListControl.value;
    if (picture === 'notAvailable') {
      this.snackBar.open('Please choose picture first', '', {
        duration: 1500,
        panelClass: 'hintMsg'
      });
    } else if (picture) {
      this.pictureList.filter(item => {
        if (item.name === picture) {
          const imgUrl = this.getImageUrl(item);
          this.displayProductData.getImage(imgUrl).subscribe(
            (v) => {
              this.dialog.open(PictureViewComponent, {
                width: '1000px',
                maxWidth: '90vw',
                minHeight: '60vh',
                maxHeight: '90vh',
                data: {
                  url: imgUrl,
                  title: item.name
                },
              });
            },
            error => {
              this.snackBar.open('This picture is not available, please notice it.', '', {
                duration: 1500,
                panelClass: 'hintMsg'
              });
            },
            () => {}
          );
        }
      });
    } else {
      this.snackBar.open('Please choose picture first', '', {
        duration: 1500,
        panelClass: 'hintMsg'
      });
    }
  }

  private getImageUrl(item: PictureItem | undefined): string {
    if (!item || !item.uuid || !item.name) {
      return '';
    }
    const extension = item.fileType ? item.fileType : 'jpg';
    const typePath = item.type.toLowerCase() === 'picture' ? 'Pictures' : 'Drawings';
    const imgUrl = `https://catalog-data-images-${environment.stage}.s3-eu-west-1.amazonaws.com/${typePath}/${item.uuid}/${item.name}.${extension}`;
    return imgUrl;
  }

  // model --> view
  writeValue(value: string): void {
    if (value && value !== '') {
      let pictureName = value;
      
      // If value is a URL, extract the picture name from it
      // URL format: https://catalog-data-images-*.s3-eu-west-1.amazonaws.com/Pictures/{uuid}/{name}.{ext}
      if (value.includes('http') || value.includes('s3')) {
        const nameMatch = value.match(/\/([^\/]+)\.[^\/]+$/);
        pictureName = nameMatch ? nameMatch[1] : value;
      }
      
      this.pictureStore = pictureName;
      this.pendingValue = pictureName; // Store for later if list not loaded
      
      // Try to update immediately, but if list not loaded it will be applied later
      if (this.listLoaded) {
        this.updatePreselection();
      }
    } else {
      this.selectListControl.reset('empty');
    }
  }

  // view --> model
  registerOnChange(fn: (value: string) => void) {
    const onChangeSubscription = this.selectListControl.valueChanges.subscribe(fn);
    this.subscriptions.push(onChangeSubscription);
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  onTouched: () => void = () => { };

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

}
